import { FC, PropsWithChildren, useCallback, useLayoutEffect, useReducer } from 'react';

import { Award } from '../../../../../../types/bonus';
import { useGiveableAwards } from './hooks/useGiveableAwards';
import { AwardContext, AwardState } from './state';

enum Stage {
	Select,
	Give,
}

enum ActionType {
	DisplayNoAward = 'display-no-award',
	SelectAward = 'select-award',
	SetHideGiver = 'hide-giver',
	SetStage = 'set-stage',
	Reset = 'reset',
	ViewAward = 'view-award',
}

type Action<T extends ActionType, P extends Record<string, unknown> | undefined = undefined> = P extends undefined ? { type: T } : { type: T } & P;

type DisplayNoAwardAction = Action<ActionType.DisplayNoAward>;

type ResetAction = Action<ActionType.Reset>;

type SelectAwardAction = Action<ActionType.SelectAward, { award: Award }>;

type SetHideGiverAction = Action<ActionType.SetHideGiver, { hideGiver: boolean }>;

type SetStageAction = Action<ActionType.SetStage, { stage: Stage }>;

type ViewAwardAction = Action<ActionType.ViewAward>;

type AwardAction = DisplayNoAwardAction | ResetAction | SelectAwardAction | SetHideGiverAction | SetStageAction | ViewAwardAction;

const initialState: AwardState = {
	group: '',
	hideGiver: false,
	isMissingSelection: false,
	selectedAward: undefined as unknown as Award,
	stage: Stage.Select,
};

const reducer = (state: AwardState, action: AwardAction): AwardState => {
	switch (action.type) {
		case ActionType.DisplayNoAward:
			return {
				...state,
				isMissingSelection: true,
			};

		case ActionType.Reset:
			return {
				...initialState,
			};

		case ActionType.SelectAward:
			return {
				...state,
				isMissingSelection: false,
				selectedAward: action.award,
			};

		case ActionType.SetStage:
			return {
				...state,
				stage: action.stage,
			};

		case ActionType.ViewAward:
			return {
				...state,
				stage: Stage.Give,
			};

		case ActionType.SetHideGiver:
			return {
				...state,
				hideGiver: action.hideGiver,
			};
	}
};

type AwardModalStateProps = PropsWithChildren<{
	isOpen: boolean;
	setIsSubmitting: (isSubmitting: boolean) => void;
}>;

export const AwardModalState: FC<AwardModalStateProps> = ({ children, isOpen, setIsSubmitting }) => {
	const [state, dispatch] = useReducer(reducer, initialState, (state) => ({
		...state,
		group: Math.random().toString(36).substring(7),
	}));

	const { awards } = useGiveableAwards();

	const resetModal = useCallback(() => {
		dispatch({ type: ActionType.Reset });
	}, []);

	const goToGiveAward = useCallback(() => {
		dispatch({
			type: state.selectedAward ? ActionType.ViewAward : ActionType.DisplayNoAward,
		});
	}, [state.selectedAward]);

	const goToSelectAward = useCallback(() => {
		dispatch({ type: ActionType.SetStage, stage: Stage.Select });
	}, []);

	const selectAward = useCallback((award: Award) => {
		dispatch({ type: ActionType.SelectAward, award });
	}, []);

	const setHideGiver = useCallback((hideGiver: boolean) => {
		dispatch({ type: ActionType.SetHideGiver, hideGiver });
	}, []);

	useLayoutEffect(() => {
		if (isOpen) {
			resetModal();
		}
	}, [isOpen, resetModal]);

	return (
		<AwardContext.Provider
			value={{
				awards,
				goToGiveAward,
				goToSelectAward,
				isTemplatedAward: !!state.selectedAward?.reason,
				setHideGiver,
				selectAward,
				setIsSubmitting,
				...state,
			}}
		>
			{children}
		</AwardContext.Provider>
	);
};
